import React, { useEffect, useState } from "react";
import { Card, Form, Row, Col, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  kycStatus,
  verifyPan,
  aadhaarSendOtp,
  aadhaarVerifyOtp,
  verifyBank,
} from "../../../services/kyc";
import { getrestrictions } from "../../../services/setting";

const StatusBadge = ({ verified }) => (
  <span
    className={`badge ${verified ? "bg-success" : "bg-warning"}`}
    style={{ fontSize: "0.75rem" }}
  >
    {verified ? "Verified" : "Not Verified"}
  </span>
);

export default function KycVerification({ memberId,status: initialStatus }) {
  const [status, setStatus] = useState(null);

  // Channels enabled in Settings. Assume all on until the call answers, so a
  // slow response never briefly hides a card that is actually available.
  const [kycAllowed, setKycAllowed] = useState({
    aadhaar: true,
    pan: true,
    bank: true,
  });

  useEffect(() => {
    getrestrictions().then((res) => {
      if (res?.status === true && res.data?.allowed_kyc_channels) {
        setKycAllowed(res.data.allowed_kyc_channels);
      }
    });
  }, []);

  // PAN
  const [pan, setPan] = useState("");
  const [panLoading, setPanLoading] = useState(false);

  // Aadhaar
  const [aadhaar, setAadhaar] = useState("");
  const [refId, setRefId] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [aadhaarLoading, setAadhaarLoading] = useState(false);

  // Bank
  const [accountNo, setAccountNo] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [bankLoading, setBankLoading] = useState(false);

  const loadStatus = () => {
    if (!memberId) return;
    kycStatus(memberId).then((res) => {
      if (res?.status === true) {
        setStatus(res.data);
        setPan(res.data.pan_no || "");
        setAadhaar(res.data.aadhar_no || "");
        setAccountNo(res.data.bank_account_no || "");
        setIfsc(res.data.bank_ifsc || "");
      }
    });
  };

  useEffect(loadStatus, [memberId]);

  const onVerifyPan = async () => {
    if (!pan) return toast.error("Enter PAN number");
    setPanLoading(true);
    const res = await verifyPan({ member_id: memberId, pan });
    setPanLoading(false);
    if (res?.status === true) {
      toast.success(res.message || "PAN verified");
      loadStatus();
    }
  };

  const onSendOtp = async () => {
    if (!aadhaar) return toast.error("Enter Aadhaar number");
    setAadhaarLoading(true);
    const res = await aadhaarSendOtp({ member_id: memberId, aadhaar_no: aadhaar });
    setAadhaarLoading(false);
    if (res?.status === true) {
      setRefId(res.data?.ref_id || "");
      setOtpSent(true);
      toast.success(res.message || "OTP sent");
    }
  };

  const onVerifyOtp = async () => {
    if (!otp) return toast.error("Enter OTP");
    setAadhaarLoading(true);
    const res = await aadhaarVerifyOtp({
      member_id: memberId,
      ref_id: refId,
      otp,
      aadhaar_no: aadhaar,
    });
    setAadhaarLoading(false);
    if (res?.status === true) {
      toast.success(res.message || "Aadhaar verified");
      setOtp("");
      setOtpSent(false);
      loadStatus();
    }
  };

  const onVerifyBank = async () => {
    if (!accountNo || !ifsc) return toast.error("Enter account number and IFSC");
    setBankLoading(true);
    const res = await verifyBank({
      member_id: memberId,
      bank_account_no: accountNo,
      bank_ifsc: ifsc,
    });
    setBankLoading(false);
    if (res?.status === true) {
      toast.success(res.message || "Bank account verified");
      loadStatus();
    }
  };

  const overall = status?.kyc_status || "pending";

  return (
    <div className="row">
      <div className="col-12 mt-3 mb-2">
        <span className="me-2 fw-semibold">Overall KYC Status:</span>
        <span
          className={`badge ${
            overall === "verified"
              ? "bg-success"
              : overall === "partial"
              ? "bg-info"
              : "bg-secondary"
          }`}
        >
          {overall.toUpperCase()}
        </span>
      </div>

      {/* PAN */}
      {kycAllowed.pan && (
      <div className="col-xl-4 col-lg-6 col-md-12">
        <Card className="custom-card">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <label className="main-content-label my-auto">PAN Verification</label>
            <StatusBadge verified={status?.pan_verified === 1} />
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label>PAN Number</Form.Label>
              <Form.Control
                type="text"
                value={pan}
                maxLength={10}
                placeholder="ABCDE1234F"
                disabled={status?.pan_verified === 1}
                onChange={(e) => setPan(e.target.value.toUpperCase())}
              />
            </Form.Group>
            {status?.pan_verified !== 1 && initialStatus == 1 && (
              <Button variant="primary" disabled={panLoading} onClick={onVerifyPan}>
                {panLoading ? "Verifying..." : "Verify PAN"}
              </Button>
            )}
          </Card.Body>
        </Card>
      </div>
      )}

      {/* Aadhaar */}
      {kycAllowed.aadhaar && (
      <div className="col-xl-4 col-lg-6 col-md-12">
        <Card className="custom-card">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <label className="main-content-label my-auto">Aadhaar Verification</label>
            <StatusBadge verified={status?.aadhaar_verified === 1} />
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label>Aadhaar Number</Form.Label>
              <Form.Control
                type="text"
                value={aadhaar}
                maxLength={12}
                placeholder="12-digit Aadhaar"
                disabled={status?.aadhaar_verified === 1 || otpSent}
                onChange={(e) => setAadhaar(e.target.value.replace(/\D/g, ""))}
              />
            </Form.Group>
            {otpSent && status?.aadhaar_verified !== 1 && initialStatus == 1 && (
              <Form.Group className="mb-3">
                <Form.Label>Enter OTP</Form.Label>
                <Form.Control
                  type="text"
                  value={otp}
                  maxLength={6}
                  placeholder="OTP"
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                />
              </Form.Group>
            )}
            {status?.aadhaar_verified !== 1 && initialStatus == 1 &&
              (otpSent ? (
                <>
                  <Button
                    variant="primary"
                    className="me-2"
                    disabled={aadhaarLoading}
                    onClick={onVerifyOtp}
                  >
                    {aadhaarLoading ? "Verifying..." : "Verify OTP"}
                  </Button>
                  <Button
                    variant="link"
                    disabled={aadhaarLoading}
                    onClick={onSendOtp}
                  >
                    Resend
                  </Button>
                </>
              ) : (
                <Button
                  variant="primary"
                  disabled={aadhaarLoading}
                  onClick={onSendOtp}
                >
                  {aadhaarLoading ? "Sending..." : "Send OTP"}
                </Button>
              ))}
          </Card.Body>
        </Card>
      </div>
      )}

      {/* Bank */}
      {kycAllowed.bank && (
      <div className="col-xl-4 col-lg-6 col-md-12">
        <Card className="custom-card">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <label className="main-content-label my-auto">Bank Verification</label>
            <StatusBadge verified={status?.bank_verified === 1} />
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label>Account Number</Form.Label>
              <Form.Control
                type="text"
                value={accountNo}
                placeholder="Bank account number"
                disabled={status?.bank_verified === 1}
                onChange={(e) => setAccountNo(e.target.value.replace(/\D/g, ""))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>IFSC Code</Form.Label>
              <Form.Control
                type="text"
                value={ifsc}
                maxLength={11}
                placeholder="IFSC"
                disabled={status?.bank_verified === 1}
                onChange={(e) => setIfsc(e.target.value.toUpperCase())}
              />
            </Form.Group>
            {status?.bank_verified === 1 && status?.bank_name && (
              <p className="text-muted mb-2">Bank: {status.bank_name}</p>
            )}
            {status?.bank_verified !== 1 && initialStatus == 1 && (
              <Button variant="primary" disabled={bankLoading} onClick={onVerifyBank}>
                {bankLoading ? "Verifying..." : "Verify Bank"}
              </Button>
            )}
          </Card.Body>
        </Card>
      </div>
      )}
    </div>
  );
}
